import { QueryClient } from '@tanstack/react-query';

// Create a client with optimized defaults for manufacturing inventory system
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time - how long data is considered fresh (5 minutes)
      staleTime: 5 * 60 * 1000,
      
      // Cache time - how long data stays in cache when not in use (10 minutes)
      gcTime: 10 * 60 * 1000,
      
      // Retry failed requests 3 times with exponential backoff
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch on window focus for critical data
      refetchOnWindowFocus: true,
      
      // Refetch on reconnect to keep data fresh
      refetchOnReconnect: 'always',
      
      // Don't refetch on mount if data is still fresh
      refetchOnMount: true,
      
      // Error handling
      throwOnError: false,
      
      // Network mode - handle offline scenarios
      networkMode: 'online'
    },
    mutations: {
      // Retry mutations only once
      retry: 1,
      
      // Throw on error for mutations to handle them in components
      throwOnError: true,
      
      // Network mode for mutations
      networkMode: 'online'
    }
  }
});

// Query key factories for consistent key management
export const queryKeys = {
  // Product-related queries
  products: {
    all: ['products'],
    lists: () => [...queryKeys.products.all, 'list'],
    list: (filters) => [...queryKeys.products.lists(), filters],
    details: () => [...queryKeys.products.all, 'detail'],
    detail: (id) => [...queryKeys.products.details(), id],
    search: (query) => [...queryKeys.products.all, 'search', query],
    categories: ['products', 'categories']
  },
  
  // Sales-related queries
  sales: {
    all: ['sales'],
    lists: () => [...queryKeys.sales.all, 'list'],
    list: (filters) => [...queryKeys.sales.lists(), filters],
    details: () => [...queryKeys.sales.all, 'detail'],
    detail: (id) => [...queryKeys.sales.details(), id],
    stats: ['sales', 'stats']
  },
  
  // Quotation-related queries
  quotations: {
    all: ['quotations'],
    lists: () => [...queryKeys.quotations.all, 'list'],
    list: (filters) => [...queryKeys.quotations.lists(), filters],
    details: () => [...queryKeys.quotations.all, 'detail'],
    detail: (id) => [...queryKeys.quotations.details(), id]
  },
  
  // Inventory-related queries
  inventory: {
    all: ['inventory'],
    lists: () => [...queryKeys.inventory.all, 'list'],
    list: (filters) => [...queryKeys.inventory.lists(), filters],
    details: () => [...queryKeys.inventory.all, 'detail'],
    detail: (id) => [...queryKeys.inventory.details(), id],
    stats: ['inventory', 'stats']
  },
  
  // User and auth-related queries
  auth: {
    user: ['auth', 'user'],
    permissions: ['auth', 'permissions'],
    users: {
      all: ['users'],
      lists: () => [...queryKeys.auth.users.all, 'list'],
      list: (filters) => [...queryKeys.auth.users.lists(), filters],
      detail: (id) => [...queryKeys.auth.users.all, 'detail', id]
    }
  },
  
  // Reports and analytics
  reports: {
    all: ['reports'],
    dashboard: ['reports', 'dashboard'],
    sales: (period) => ['reports', 'sales', period],
    inventory: (period) => ['reports', 'inventory', period],
    financial: (period) => ['reports', 'financial', period]
  }
};

// Mutation key factories
export const mutationKeys = {
  products: {
    create: 'products-create',
    update: 'products-update',
    delete: 'products-delete',
    bulkUpdate: 'products-bulk-update'
  },
  sales: {
    create: 'sales-create',
    update: 'sales-update',
    delete: 'sales-delete'
  },
  quotations: {
    create: 'quotations-create',
    update: 'quotations-update',
    delete: 'quotations-delete',
    convertToSales: 'quotations-convert-to-sales'
  },
  auth: {
    login: 'auth-login',
    logout: 'auth-logout',
    register: 'auth-register',
    updateProfile: 'auth-update-profile'
  }
};

// Error boundary for React Query errors
export const queryErrorHandler = (error, query) => {
  console.error('React Query Error:', {
    error,
    queryKey: query?.queryKey,
    queryHash: query?.queryHash
  });
  
  // Log specific error types
  if (error?.response?.status === 401) {
    console.warn('Authentication error - redirecting to login');
    // Could trigger auth redirect here
  } else if (error?.response?.status >= 500) {
    console.error('Server error:', error?.response?.data);
    // Could show global error notification
  }
};

// Default query options for specific data types
export const getQueryOptions = (type) => {
  const options = {
    // Real-time data (frequent updates needed)
    realtime: {
      staleTime: 30 * 1000, // 30 seconds
      gcTime: 2 * 60 * 1000, // 2 minutes
      refetchInterval: 60 * 1000, // 1 minute
      refetchOnWindowFocus: true
    },
    
    // Static data (rarely changes)
    static: {
      staleTime: 30 * 60 * 1000, // 30 minutes
      gcTime: 60 * 60 * 1000, // 1 hour
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    },
    
    // User data (moderate update frequency)
    user: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 15 * 60 * 1000, // 15 minutes
      refetchOnWindowFocus: true
    },
    
    // Analytics/reports (can be cached longer)
    analytics: {
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false
    }
  };
  
  return options[type] || options.user;
};